import React, { useEffect } from 'react';
import './App.css';
import './lib/authAxios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import CallNotification from './pages/CallNotification';
import TechLogin from './pages/TechLogin';
import TechDashboard from './pages/TechDashboard';
import TechJobDetails from './pages/TechJobDetails';
import TechSchedule from './pages/TechSchedule';
import TechCreateJob from './pages/TechCreateJob';
import TechAddItems from './pages/TechAddItems';
import TechApproval from './pages/TechApproval';
import TechMap from './pages/TechMap';
import TechWorkOrder from './pages/TechWorkOrder';
import ContactDetail from './pages/ContactDetail';
// The real root cause of "hit Create Job and nothing happened" (2026-08-13):
// every toast() call in this app goes through useToast() (hooks/use-toast.js),
// but this file was rendering the UNRELATED sonner library's <Toaster/>
// instead of components/ui/toaster.jsx's <Toaster/> -- the one actually
// wired to useToast()'s state. Nothing in the app ever calls sonner's own
// toast() function (confirmed: only this file and its own wrapper import
// 'sonner' at all), so sonner's <Toaster/> has only ever rendered an empty,
// permanently-unused notification region. Every success/error toast across
// all 10 pages that call useToast() (TechCreateJob, TechJobDetails,
// TechWorkOrder, TechApproval, Dashboard, etc.) has been silently swallowed
// since this mismatch was introduced -- this is a same-symptom, systemic
// version of the bug, not unique to job creation.
import { Toaster } from './components/ui/toaster';
import { allowLeave } from './lib/leaveGuard';

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  const userRole = localStorage.getItem('userRole');
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (userRole === 'tech') return <Navigate to="/tech/dashboard" replace />;
  return children;
};

// An admin session (e.g. John's login) can reach every tech-portal page too --
// the backend already accepts either session type on every CRM/tech endpoint
// (_require_any_session in tech-backend/server.py draws no distinction), this
// route guard was the only thing actually locking an admin out of /tech/*.
const TechProtectedRoute = ({ children }) => {
  const isTech = localStorage.getItem('techAuthenticated');
  const isAdmin = localStorage.getItem('isAuthenticated') && localStorage.getItem('userRole') !== 'tech';
  return (isTech || isAdmin) ? children : <Navigate to="/tech" replace />;
};

// Some pages (e.g. line-item editing) are useful from both the tech app and the
// admin CRM — accept either a tech session or an admin session.
const AnyAuthProtectedRoute = ({ children }) => {
  const isTech = localStorage.getItem('techAuthenticated');
  const isAdmin = localStorage.getItem('isAuthenticated') && localStorage.getItem('userRole') !== 'tech';
  return (isTech || isAdmin) ? children : <Navigate to="/" replace />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/admin" element={<Navigate to="/" replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gray-50">
                  <div className="md:ml-64 p-4 md:p-8">
                    {/* Mobile top navigation */}
                    <div className="md:hidden flex items-center justify-between mb-4 pb-2 border-b">
                      <button
                        onClick={() => { allowLeave(); window.location.href = '/dashboard'; }}
                        className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span className="font-semibold text-sm">Dashboard</span>
                      </button>
                      <span className="font-bold text-gray-900">Calendar</span>
                      <div className="w-5" />
                    </div>
                    <Calendar />
                  </div>
                  <div className="hidden md:block fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200">
                    <div className="p-6">
                      <div className="flex items-center space-x-3 mb-8">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            <path d="M8 11h8v2H8z"/>
                            <path d="M11 8h2v8h-2z"/>
                          </svg>
                        </div>
                        <span className="text-xl font-bold text-gray-900">AAA Irrigation</span>
                      </div>
                      <button
                        onClick={() => { allowLeave(); window.location.href = '/dashboard'; }}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span className="font-medium">Back to Dashboard</span>
                      </button>
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          
          {/* Contact detail */}
          <Route path="/contacts/:id" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />

          {/* Field tech map (admin) */}
          <Route path="/map" element={<ProtectedRoute><TechMap /></ProtectedRoute>} />

          {/* 3CX Call Notification Route */}
          <Route path="/call" element={<CallNotification />} />

          {/* Tech App Routes */}
          <Route path="/tech" element={<TechLogin />} />
          <Route
            path="/tech/dashboard"
            element={
              <TechProtectedRoute>
                <TechDashboard />
              </TechProtectedRoute>
            }
          />
          <Route
            path="/tech/job/:jobId"
            element={
              <TechProtectedRoute>
                <TechJobDetails />
              </TechProtectedRoute>
            }
          />
          <Route
            path="/tech/schedule"
            element={
              <TechProtectedRoute>
                <TechSchedule />
              </TechProtectedRoute>
            }
          />
          <Route
            path="/tech/create-job"
            element={
              <TechProtectedRoute>
                <TechCreateJob />
              </TechProtectedRoute>
            }
          />
          <Route
            path="/tech/job/:jobId/add-items"
            element={
              <AnyAuthProtectedRoute>
                <TechAddItems />
              </AnyAuthProtectedRoute>
            }
          />
          <Route
            path="/tech/job/:jobId/approval"
            element={
              <TechProtectedRoute>
                <TechApproval />
              </TechProtectedRoute>
            }
          />
          <Route
            path="/tech/workorder/:woId"
            element={
              <AnyAuthProtectedRoute>
                <TechWorkOrder />
              </AnyAuthProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;